import { Utils } from '@wix/common-pro-gallery-worker-wrapper';
import {
  serverItemsToProGallery,
  v3ItemsToV1Items,
} from '@wix/pro-gallery-items-formatter';
import { AppSettings, Consts } from '@wix/photography-client-lib';
import { GALLERY_CONSTS } from '@wix/pro-gallery-lib-old';
import { getDraftGallery } from '@wix/ambassador-pro-gallery-v3-draft-gallery/http';
import {
  getDraftItem,
  queryDraftItems,
} from '@wix/ambassador-pro-gallery-v3-draft-item/http';
import { queryGalleries } from '@wix/ambassador-pro-gallery-v3-gallery/http';
import { getItem, queryItems } from '@wix/ambassador-pro-gallery-v3-item/http';

class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.reject = (...args) => {
        this.isPending = false;
        reject(...args);
      };
      this.resolve = (...args) => {
        this.isPending = false;
        resolve(...args);
      };
      this.isPending = true;
    });
  }
}

export default class V3Fetcher {
  constructor({
    context,
    controllerConfig,
    reportBiLog,
    SSRWorkerLog,
    withWarmupData,
    httpClient,
    errorHandler,
  }) {
    this.httpClient = httpClient;
    this.SSRWorkerLog = SSRWorkerLog;
    this.withWarmupData = withWarmupData;
    this.ITEMS_BATCH_SIZE = 25;
    this.controllerConfig = controllerConfig;
    this.wixCodeApi = controllerConfig.wixCodeApi;
    this.viewMode = Utils.parseViewMode(this.wixCodeApi.window.viewMode);
    this.context = context.getContext();
    this.baseUrl = Utils.getBaseUrl(this.wixCodeApi, this.viewMode);
    this.appSettingsBaseUrl =
      this.viewMode === GALLERY_CONSTS.viewMode.PREVIEW ||
      this.viewMode === GALLERY_CONSTS.viewMode.EDIT
        ? ''
        : this.baseUrl;
    this.SSRWorkerLog.push('appSettingsBaseUrl: ' + this.appSettingsBaseUrl);

    this.withErrorHandler = errorHandler?.withErrorHandler;
    this.getResolvedError = errorHandler?.getResolvedError;
    this.showError = errorHandler?.showError;

    this.compId = controllerConfig.compId;
    this.externalId =
      controllerConfig.config.externalId ||
      '00000000-0000-0000-0000-000000000000';
    this.instance = controllerConfig.appParams.instance;
    this.instanceId = controllerConfig.appParams.instanceId;
    this.appSettings = new AppSettings(
      this.compId,
      this.instance,
      this.viewMode,
      this.appSettingsBaseUrl,
    );
    this.reportBiLog = reportBiLog;
    this.initDataSavedState();
  }

  isSSR() {
    return this.wixCodeApi.window.rendering.env === 'backend';
  }

  isDraftMode() {
    return (
      this.viewMode === GALLERY_CONSTS.viewMode.PREVIEW ||
      this.viewMode === GALLERY_CONSTS.viewMode.EDIT
    );
  }

  setAppSettings(appSettings) {
    Object.assign(this, { ...appSettings });
    this.appSettingsData = appSettings;
    this.SSRWorkerLog.push(
      'FLOW received appSettings and the gallery Id is: ',
      appSettings.galleryId,
    );
  }

  fetchSettings() {
    if (!this.appSettingsFetchingPromise?.isPending) {
      this.appSettingsFetchingPromise = new Deferred();
      this.SSRWorkerLog.push('FLOW trying to get settings from appSettings');
      this.withWarmupData(
        () => this.appSettings.get(this.dataSavedState),
        'appSettings',
      ).then((appSettings) => {
        if (appSettings && appSettings.galleryId) {
          this.setAppSettings(appSettings);
          this.appSettingsFetchingPromise.resolve(appSettings);
        } else {
          this.appSettingsFetchingPromise.resolve(null);
          if (!this.isSSR()) {
            const biData = {
              compId: this.compId,
              errorType: 'no_appSettings.galleryId',
            };
            this.reportBiLog('proGalleryErrorUou', biData);
          }
        }
      });
    } else {
      this.SSRWorkerLog.push('appSettings is already fetching');
    }
    return this.appSettingsFetchingPromise.promise;
  }

  async fetchGalleryId() {
    if (this.galleryId) {
      return;
    }
    const settings = await this.fetchSettings();
    if (!settings) {
      console.error('could not get galleryId for pro gallery');
    }
    return;
  }

  initDataSavedState() {
    if (
      this.viewMode === GALLERY_CONSTS.viewMode.PREVIEW ||
      this.viewMode === GALLERY_CONSTS.viewMode.EDIT
    ) {
      const encodedInstance = this.instance.substring(
        this.instance.indexOf('.') + 1,
      );
      const isDemoMode = JSON.parse(atob(encodedInstance)).demoMode;
      this.dataSavedState = isDemoMode
        ? Consts.dataSavedState.PUBLISHED
        : Consts.dataSavedState.SAVED;
    } else {
      this.dataSavedState = Consts.dataSavedState.PUBLISHED;
    }
  }

  setGalleryData({ gallery, settings }) {
    const { created: dateCreated } = gallery;
    this.dateCreated = dateCreated;
    this.setSettings(settings);
  }

  setGalleryId(galleryId) {
    this.galleryId = galleryId;
  }

  setSettings(settings) {
    const parseSettings = (_settings) => {
      if (typeof _settings === 'string') {
        try {
          return JSON.parse(_settings);
        } catch (e) {
          console.error('Failed parse settting', e);
          return _settings;
        }
      }
      return _settings;
    };
    const prevSettings = parseSettings(this.settings);
    const nextSettings = parseSettings(settings);
    this.settings = { ...prevSettings, ...nextSettings };
  }

  normalizeGalleryResponse(gallery) {
    if (!gallery) {
      return gallery;
    }
    return {
      ...gallery,
      totalItemsCount: gallery.totalItemsCount ?? gallery.totalItems,
      created: gallery.created ?? gallery.createdDate,
    };
  }

  async httpRequest(requestFactory) {
    return this.withErrorHandler
      ? this.withErrorHandler(() => this.httpClient.request(requestFactory))
      : this.httpClient.request(requestFactory);
  }

  getGalleryRequest() {
    return queryGalleries({
      query: {
        filter: {
          draftGalleryId: this.galleryId,
          variant: 'LIVE',
        },
      },
    });
  }

  getDraftGalleryRequest() {
    return getDraftGallery({ draftGalleryId: this.galleryId });
  }

  getItemsQueryRequest({ from, batchSize }) {
    const cursor = from > 0 ? this.itemsQueryCursors?.[from] : undefined;
    const query = cursor
      ? {
          cursorPaging: {
            limit: batchSize,
            cursor,
          },
        }
      : {
          filter: this.isDraftMode()
            ? { draftGalleryId: this.galleryMetadata.id }
            : { galleryId: this.galleryMetadata.id },
          sort: [{ fieldName: 'sortOrder', order: 'ASC' }],
          cursorPaging: {
            limit: batchSize,
          },
        };

    if (this.isDraftMode()) {
      return queryDraftItems({ query });
    }
    return queryItems({ query });
  }

  async fetchGalleryMetadata() {
    if (this.galleryMetadata) {
      return this.galleryMetadata;
    }
    let gallery;
    if (this.isDraftMode()) {
      const response = await this.httpRequest(this.getDraftGalleryRequest());
      gallery = response?.data?.draftGallery;
    } else {
      const response = await this.httpRequest(this.getGalleryRequest());
      gallery = response?.data?.galleries[0];
    }
    this.galleryMetadata = this.normalizeGalleryResponse(gallery);
    return this.galleryMetadata;
  }

  storeItemsQueryCursor({ from, batchSize, pagingMetadata }) {
    const nextCursor = pagingMetadata?.cursors?.next;
    if (nextCursor) {
      if (!this.itemsQueryCursors) {
        this.itemsQueryCursors = {};
      }
      this.itemsQueryCursors[from + batchSize] = nextCursor;
    }
  }

  async getGalleryDataFromServer({ from, batchSize = this.ITEMS_BATCH_SIZE }) {
    this.SSRWorkerLog.push('Fn getGalleryDataFromServer', 'from: ', from);
    try {
      return await this.getDataFromServer({
        from,
        batchSize,
      });
    } catch (e) {
      try {
        const resolvedError = this.getResolvedError
          ? this.getResolvedError(e)
          : e;
        if (this.showError) {
          this.showError(resolvedError);
        }
      } catch (_) {}
      throw new Error(
        'Could not get items for gallery comp:' +
          this.compId +
          ' galleryId: ' +
          this.galleryId,
      );
    }
  }

  async getDataFromServer({ from, batchSize }) {
    await this.fetchGalleryId();
    if (!this.galleryId) {
      throw new Error(
        'no galleryId, can not fetch items from pro gallery server',
      );
    }
    const gallery = await this.fetchGalleryMetadata();
    const itemsResponse = await this.httpRequest(
      this.getItemsQueryRequest({ from, batchSize }),
    );

    this.storeItemsQueryCursor({
      from,
      batchSize,
      pagingMetadata: itemsResponse?.data?.pagingMetadata,
    });

    const items =
      itemsResponse?.data?.draftItems || itemsResponse?.data?.items || [];
    this.setGalleryData({ gallery, settings: this.appSettingsData });
    return {
      items: serverItemsToProGallery(v3ItemsToV1Items(items)),
      totalItemsCount: gallery.totalItemsCount,
    };
  }

  getItemRequest(itemId) {
    if (this.isDraftMode()) {
      return getDraftItem({ draftItemId: itemId });
    }
    return getItem({ itemId });
  }

  async getItemByIdFromServer(itemId) {
    await this.fetchGalleryId();
    try {
      const response = await this.httpRequest(this.getItemRequest(itemId));
      this.SSRWorkerLog.push(
        'trying to get Direct fullscreen item from the server ',
        response.config.url,
      );
      const data = response?.data;
      const item = data?.item || data?.draftItem;
      return item ? serverItemsToProGallery(v3ItemsToV1Items([item]))[0] : {};
    } catch (e) {
      try {
        const resolvedError = this.getResolvedError
          ? this.getResolvedError(e)
          : e;
        if (this.showError) {
          this.showError(resolvedError);
        }
      } catch (_) {}
      throw e;
    }
  }
}
